// SME Capture Flow — Phase 5 stage review (spec §4 Phase 5).
//
// The system proposes where the process naturally divides into stages; the
// expert confirms or adjusts — rename a stage, or nudge the boundary so an
// event changes sides. Never a blank prompt: the expert always reacts to a
// proposal. The shell owns the draft stages; this renders and emits edits.

import React from 'react';
import { TimelineEntry } from '../utils/captureTimeline';
import { StageEntry, moveStageBoundary, renameStage } from '../utils/captureStages';
import '../styles/CaptureStages.css';

interface Props {
  timeline: TimelineEntry[];
  stages: StageEntry[];
  disabled?: boolean;
  onChange: (next: StageEntry[]) => void;
}

const CaptureStages: React.FC<Props> = ({ timeline, stages, disabled = false, onChange }) => {
  const textOf = new Map(timeline.map((e) => [e.id, e.text]));
  // Global step numbers: events keep their position in the full timeline.
  const numberOf = new Map(timeline.map((e, i) => [e.id, i + 1]));

  return (
    <div className="capture-stages">
      {stages.map((s, i) => (
        <section key={s.id} className="capture-stage">
          <div className="capture-stage-head">
            <input
              className="capture-stage-label"
              value={s.label}
              disabled={disabled}
              onChange={(ev) => onChange(renameStage(stages, s.id, ev.target.value))}
            />
            {i > 0 && (
              <div className="capture-stage-boundary">
                <button
                  onClick={() => onChange(moveStageBoundary(stages, i, 'up'))}
                  disabled={disabled || stages[i - 1].eventIds.length < 2}
                  title="Start this stage one step earlier"
                >
                  ↑ starts earlier
                </button>
                <button
                  onClick={() => onChange(moveStageBoundary(stages, i, 'down'))}
                  disabled={disabled || s.eventIds.length < 2}
                  title="Start this stage one step later"
                >
                  ↓ starts later
                </button>
              </div>
            )}
          </div>
          <ul className="capture-stage-events">
            {s.eventIds.map((id) => (
              <li key={id}>
                <span className="capture-stage-num">{numberOf.get(id) ?? '?'}</span>
                {textOf.get(id) ?? '(missing step)'}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default CaptureStages;
